import type { AxiosResponse } from 'axios';

import client from 'api/client';
import type { AdminUsers } from 'api/models/response/AdminUsers';
import type { AdminConsultantsResponse } from 'types';

export function getAdminUsers(
    pageNum?: number,
    pageSize?: number,
    name?: string,
    company_name?: string
): Promise<AxiosResponse<AdminUsers[]>> {
    let path = `/admin/users`;
    if (pageNum != null || pageSize != null || name != null || company_name != null) {
        path += `?`;
    }
    if (pageNum != null) {
        path += `page=${pageNum}&`;
    }
    if (pageSize != null) {
        path += `pageSize=${pageSize}&`;
    }
    if (name != null) {
        path += `&name=${name}&`;
    }
    if (company_name != null) {
        path += `company_name=${company_name}&`;
    }

    return client.get<AdminUsers[]>(path);
}

/**
 * @deprecated The legacy admin endpoint generated and emailed a new plaintext
 * password. The UI now triggers a one-time, expiring reset link instead via
 * `api.forgotPassword.post({ email })` (see AdminTable). Kept only for
 * backward compatibility; do not use for new code.
 */
export function resetPassword(cliendId: string | number) {
    return client.get(`/admin/reset-password/${cliendId}`);
}

export type UserProfileUpdatePayload = {
    firstname?: string;
    lastname?: string;
    email?: string;
    external_id?: string | null;
};

export function updateUserProfile(userId: number, payload: UserProfileUpdatePayload) {
    return client.put(`/admin/user/${userId}/profile`, payload);
}

export function getConsultants(
    page = 1,
    pageSize = 100
): Promise<AxiosResponse<AdminConsultantsResponse>> {
    return client.get<AdminConsultantsResponse>('/admin/consultants', {
        params: { page, pageSize },
    });
}

// MyAliro Billing Admin APIs

interface BillingSubscription {
    id: number;
    service_provider: { id: number; name: string };
    package_type: string;
    status: string;
    monthly_fee: number;
    currency: string;
    start_date: string;
    free_period_end: string | null;
    created_at: string;
}

interface BillingDispute {
    id: number;
    ticket_number: string;
    service_provider: { id: number; name: string };
    invoice_number: string;
    subject: string;
    status: string;
    adjustment_amount: number;
    created_at: string;
    resolved_at: string | null;
}

interface BillingDashboardMetrics {
    active_subscriptions: number;
    pending_approvals: number;
    outstanding_amount: number;
    open_disputes: number;
    monthly_revenue: number;
}

interface ApproveSubscriptionResponse {
    message: string;
}

interface ResolveDisputeResponse {
    id: number;
    status: string;
    message: string;
}

interface GenerateInvoiceResponse {
    invoice_id: number;
    invoice_number: string;
    total_amount: number;
    message: string;
}

export function getBillingSubscriptions(
    page: number,
    pageSize: number,
    status?: string,
    packageType?: string
): Promise<AxiosResponse<BillingSubscription[]>> {
    return client.get('/admin/billing/subscriptions', {
        params: { page, per_page: pageSize, status, package_type: packageType },
    });
}

export function approveSubscription(
    subscriptionId: number,
    approved: boolean
): Promise<AxiosResponse<ApproveSubscriptionResponse>> {
    return client.put(`/admin/billing/subscriptions/${subscriptionId}/approve`, { approved });
}

export function getBillingDisputes(
    page: number,
    pageSize: number,
    status?: string
): Promise<AxiosResponse<BillingDispute[]>> {
    return client.get('/admin/billing/disputes', {
        params: { page, per_page: pageSize, status },
    });
}

export function resolveDispute(
    disputeId: number,
    adjustmentAmount: number,
    adjustmentReason: string,
    resolutionNotes: string
): Promise<AxiosResponse<ResolveDisputeResponse>> {
    return client.put(`/admin/billing/disputes/${disputeId}/resolve`, {
        adjustment_amount: adjustmentAmount,
        adjustment_reason: adjustmentReason,
        resolution_notes: resolutionNotes,
    });
}

export function getBillingDashboard(): Promise<AxiosResponse<BillingDashboardMetrics>> {
    return client.get('/admin/billing/dashboard');
}

export function manuallyGenerateInvoice(
    subscriptionId: number
): Promise<AxiosResponse<GenerateInvoiceResponse>> {
    return client.post('/admin/billing/invoices/generate', { subscription_id: subscriptionId });
}
